import { forwardRef, type ButtonHTMLAttributes } from 'react'

export type IconCloseButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> & {
  /**
   * Each location uses this button for a different action ("Remove X",
   * "Close configurator", "Dismiss dialog"), so the accessible name has to
   * come from the caller — never bake in a generic default like "Close".
   */
  'aria-label': string
}

/**
 * The circled-X close glyph used wherever the app needs a small destructive
 * or dismiss action — Overview row "remove", LineConfigurator "close",
 * future cart row removes, etc.
 *
 * SVG matches the legacy spare-parts cart row + shared Dialog close so the
 * visual is identical everywhere we render this affordance. Hover / focus
 * styling lives in `IconCloseButton.scss` under `.al-icon-close`.
 */
export const IconCloseButton = forwardRef<HTMLButtonElement, IconCloseButtonProps>(
  function IconCloseButton({ className = '', type = 'button', ...rest }, ref) {
    const cls = ['al-icon-close', className].filter(Boolean).join(' ')
    return (
      <button ref={ref} type={type} className={cls} {...rest}>
        <svg
          width="22"
          height="22"
          viewBox="0 0 38 38"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M19 38C8.5 38 0 29.5 0 19S8.5 0 19 0s19 8.5 19 19-8.5 19-19 19zm0-36.5C9.4 1.5 1.5 9.4 1.5 19S9.4 36.5 19 36.5 36.5 28.7 36.5 19 28.7 1.5 19 1.5z" />
          <path d="M24.2 24.9c-.2 0-.4-.1-.5-.2L13.3 14.4c-.3-.3-.3-.8 0-1.1s.8-.3 1.1 0l10.3 10.3c.3.3.3.8 0 1.1-.1.1-.3.2-.5.2z" />
          <path d="M13.9 24.9c-.2 0-.4-.1-.5-.2-.3-.3-.3-.8 0-1.1l10.3-10.3c.3-.3.8-.3 1.1 0s.3.8 0 1.1L14.4 24.7c-.2.1-.4.2-.5.2z" />
        </svg>
      </button>
    )
  },
)
